import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { NotificationProfileModel, Partner } from '@core/core.types';
import { Logger } from '@core/services/logger.service';
import { ProductUtilsService } from '@core/services/product-utils.service';
import { LoadBundleConfigurationByIds } from '@core/store/bundle-configuration/bundle-configuration.actions';
import { BundleConfigurationsState } from '@core/store/bundle-configuration/bundle-configuration.state';
import { LoadNotificationProfilesAction } from '@core/store/notification-profiles/notification-profiles.actions';
import { NotificationProfileState } from '@core/store/notification-profiles/notification-profiles.state';
import { Util } from '@core/utils/core.util';
import { Select, Store } from '@ngxs/store';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { Observable, combineLatest, forkJoin, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import { NOTIFICATION_EMAILS_CONSTANTS } from '../../app.constants';
import { Country } from '../@core/core.types';
import { AccountUtilsService } from '../@core/services/account-utils.service';
import { PartnersService } from '../@core/services/partners.service';
import { CountriesState } from '../@core/store/countries/countries.state';
import { PartnerTypeState } from '../@core/store/partner-types/partner-types.state';
import {
  LoadPartnerById,
  LoadPartnersByIds,
} from '../@core/store/partners/partners.actions';
import { PartnersState } from '../@core/store/partners/partners.state';
import { ProductsListState } from '../@core/store/products/products.state';

@Injectable({
  providedIn: 'root',
})
export class PartnersUtilsService {
  private store = inject(Store);
  private partnersService = inject(PartnersService);
  private productUtils = inject(ProductUtilsService);
  private accountUtils = inject(AccountUtilsService);
  private toastService = inject(ToastService);

  @Select(CountriesState.getCountries) countries$: Observable<Country[]>;
  private readonly log = new Logger(this.constructor.name);

  getPartnerProducts$(partner: Partner): Observable<any[]> {
    this.log.debug('get partner products');
    if (!partner || !partner.productConfigurations) {
      return of(null);
    }
    if (!partner.productConfigurations.length) {
      return of([]);
    }
    const productsConfigurations$ = combineLatest(
      partner.productConfigurations.map((pcId) =>
        this.productUtils.getProductConfigurationById$(pcId)
      )
    );
    return combineLatest([
      this.store.select(ProductsListState.getProductsList),
      productsConfigurations$,
    ]).pipe(
      map(([products, productsConfigurations]) =>
        productsConfigurations.map((pc) =>
          pc && pc.productId
            ? products.find((p) => p.id === pc.productId)
            : null
        )
      )
    );
  }

  getPartnerDoctors$(partner: Partner): Observable<Partner[]> {
    if (partner && partner.subPartners) {
      if (!partner.subPartners.length) {
        return of([]);
      }

      return forkJoin(
        Util.chunkArray(partner.subPartners, 100).map((chunk) =>
          this.store.dispatch(new LoadPartnersByIds(chunk))
        )
      ).pipe(
        switchMap(() =>
          this.store
            .select(PartnersState.getPartnersByIds)
            .pipe(
              map((getByPartnerIds) => getByPartnerIds(partner.subPartners))
            )
        )
      );
    }
    return of(null);
  }

  getPartnerById$(partnerId: string, refresh = false): Observable<Partner> {
    this.log.debug('get partner by id');

    if (!partnerId) {
      return of(null);
    }

    return this.store.dispatch(new LoadPartnerById(partnerId, refresh)).pipe(
      switchMap(() =>
        this.store.select(PartnersState.getPartnerById).pipe(
          map((filterById: (id: string) => Partner) => filterById(partnerId)),
          filter((p) => p !== undefined)
        )
      ),
      distinctUntilChanged()
    );
  }

  getPartnerByIdSignal(partnerId: string, refresh = false): Signal<Partner> {
    if (!partnerId) {
      return signal(null);
    }

    this.store.dispatch(new LoadPartnerById(partnerId, refresh));

    return computed(() =>
      this.store.selectSignal(PartnersState.getPartnerById)()(partnerId)
    );
  }

  getSubPartnerById$(
    subPartnerId: string,
    refresh = false
  ): Observable<Partner> {
    return this.store.dispatch(new LoadPartnerById(subPartnerId, refresh)).pipe(
      switchMap(() =>
        this.store.select(PartnersState.getPartnerById).pipe(
          map((filterById) => filterById(subPartnerId)),
          filter((d) => d !== undefined)
        )
      ),
      distinctUntilChanged()
    );
  }

  getPartnersBySearchString(
    searchString: string,
    ids?: string[]
  ): Observable<Partner[]> {
    this.log.debug('get partners by search string');
    return combineLatest([
      this.store.select(PartnerTypeState.getFirstLevelPartnerTypesIdsList),
      this.accountUtils.getRoleCountries$(),
      this.accountUtils.getAccountManagerIds$(),
    ]).pipe(
      take(1),
      switchMap(([partnerTypeids, roleCountries, accountManagerIds]) =>
        this.partnersService.findPartners(
          partnerTypeids,
          searchString,
          ids,
          roleCountries,
          accountManagerIds
        )
      )
    );
  }

  filterPartnerCountries$(): Observable<Country[]> {
    return combineLatest([
      this.accountUtils.getRoleCountries$(),
      this.store.select(CountriesState.getAccountManagerCountries),
      this.countries$,
    ]).pipe(
      map(([roleCountries, accountManagerCountries, countries]) => {
        if (accountManagerCountries) {
          accountManagerCountries = accountManagerCountries.map((c) =>
            c.toLowerCase()
          );
          countries = countries.filter(
            (c) => accountManagerCountries.indexOf(c.name.toLowerCase()) > -1
          );
        } else if (roleCountries) {
          countries = countries.filter(
            (c) => roleCountries.indexOf(c.name.toLowerCase()) > -1
          );
        }
        return countries;
      })
    );
  }

  getDoctorsIdsBySearchToken(
    doctorNameToken: string,
    limit = false
  ): Observable<any> {
    this.log.debug('get doctors ids by search token');
    if (doctorNameToken && doctorNameToken.length > 0) {
      return this.partnersService
        .getPartnersByNametoken(doctorNameToken.trim())
        .pipe(
          take(200),
          switchMap((doctors) => {
            if (doctors && doctors.length > 0) {
              if (doctors.length <= 40 || !limit) {
                return of(doctors.slice(0, 300).map((d) => d.id));
              } else {
                this.toastService.danger(
                  'Too many doctors, please filter more precisely',
                  'Filter error'
                );
                return of(null);
              }
            }
            return of(null);
          })
        );
    }
    return of(null);
  }

  getDoctorNotificationProfiles(
    doctorId: string,
    refresh = false
  ): Observable<NotificationProfileModel[]> {
    this.log.debug('get doctor notification profiles', doctorId);
    return this.getSubPartnerById$(doctorId, refresh).pipe(
      switchMap((doctor) => {
        if (
          !doctor ||
          !doctor.notificationProfiles ||
          !doctor.notificationProfiles.length
        ) {
          return of([]);
        }
        return this.store
          .dispatch(
            new LoadNotificationProfilesAction(doctor.notificationProfiles)
          )
          .pipe(
            switchMap(() =>
              this.store
                .select(NotificationProfileState.getNotificationProfiles)
                .pipe(
                  map((filterById) => filterById(doctor.notificationProfiles))
                )
            )
          );
      })
    );
  }

  getResultReferenceLinkTemplateByLanguage(lang: string) {
    switch (lang.toLowerCase()) {
      case 'slovenian':
        return NOTIFICATION_EMAILS_CONSTANTS.resultReferenceLinkSl;
      case 'croatian':
        return NOTIFICATION_EMAILS_CONSTANTS.resultReferenceLinkHr;
      case 'polish':
        return NOTIFICATION_EMAILS_CONSTANTS.resultReferenceLinkPl;
      default:
        return NOTIFICATION_EMAILS_CONSTANTS.resultReferenceLinkEn;
    }
  }

  getPatientFormLinkTemplateByLanguage(lang: string) {
    switch (lang.toLowerCase()) {
      case 'spanish':
        return NOTIFICATION_EMAILS_CONSTANTS.customerFormInviteMx;
      case 'serbian':
        return NOTIFICATION_EMAILS_CONSTANTS.customerFormInviteRs;
      default:
        return NOTIFICATION_EMAILS_CONSTANTS.customerFormInvitePl;
    }
  }

  public getPartnerBundles(bundleConfigurationIds: string[]) {
    return this.getPartnerBundleIds(bundleConfigurationIds).pipe(
      switchMap((bundleIds: string[]) =>
        this.store
          .select(ProductsListState.getBundles)
          .pipe(map((getBundles) => getBundles(bundleIds)))
      )
    );
  }

  private getPartnerBundleIds(
    bundleConfigurationIds: string[]
  ): Observable<string[]> {
    return this.store
      .dispatch(new LoadBundleConfigurationByIds(bundleConfigurationIds))
      .pipe(
        switchMap(() =>
          this.store
            .select(BundleConfigurationsState.getBundleConfigurationByIds)
            .pipe(
              map((getBundleConfigurationByIds) => {
                return getBundleConfigurationByIds(bundleConfigurationIds).map(
                  (bundleConfiguration) => bundleConfiguration.bundleId
                );
              })
            )
        )
      );
  }
}
